import { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import RegistrationPdfModal from '@/components/dashboard/modals/RegistrationPdfModal'
import QRCode from 'qrcode'

interface Props {
  parishName?: string
  sharecode?: string
}

export default function RegistrationBlock({ parishName, sharecode }: Props) {
  const [qr, setQr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [showPdf, setShowPdf] = useState(false)

  const registrationUrl = useMemo(() => {
    if (!sharecode) return null
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    return `${origin}/register?code=${encodeURIComponent(sharecode)}`
  }, [sharecode])

  useEffect(() => {
    let isMounted = true
    const makeQr = async () => {
      if (!registrationUrl) return setQr(null)
      try {
        const dataUrl = await QRCode.toDataURL(registrationUrl, { margin: 1, scale: 6 })
        if (isMounted) setQr(dataUrl)
      } catch {
        if (isMounted) setQr(null)
      }
    }
    makeQr()
    return () => { isMounted = false }
  }, [registrationUrl])

  return (
    <motion.div
      className="mt-6 p-5 rounded-xl border border-gray-200 bg-white shadow-sm"
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
    >
      <div className="flex items-start justify-between gap-6">
        <div>
          <h3 className="text-lg font-semibold">Registration</h3>
          <p className="text-sm text-gray-600 mt-1">
            Share this link or print a flyer for families to register.
          </p>
          <div className="mt-3">
            <div className="text-sm text-gray-700">
              <span className="font-medium">Parish:</span> {parishName || '—'}
            </div>
            <div className="text-sm text-gray-700">
              <span className="font-medium">Sharecode:</span> {sharecode || '—'}
            </div>
            <div className="text-sm text-gray-700 truncate">
              <span className="font-medium">URL:</span> {registrationUrl || '—'}
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              className="btn-secondary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]"
              aria-live="polite"
              disabled={!registrationUrl}
              onClick={async () => {
                if (!registrationUrl) return
                try {
                  await navigator.clipboard.writeText(registrationUrl)
                  setCopied(true)
                  setTimeout(() => setCopied(false), 1200)
                } catch {
                  // ignore
                }
              }}
              title="Copy the registration URL to share with parents via email, text, or social media"
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
            <button
              className="btn-secondary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]"
              disabled={!registrationUrl}
              onClick={() => setShowPdf(true)}
              title="Generate and download a PDF flyer with your parish details, schedule, and registration information"
            >
              Print Registration Page
            </button>
            <button
              className="btn-secondary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]"
              disabled={!qr}
              onClick={() => {
                if (!qr) return
                const a = document.createElement('a')
                a.href = qr
                a.download = `registration-qr-${sharecode || 'parish'}.png`
                a.click()
              }}
            >
              Download QR
            </button>
          </div>
        </div>
        <div className="w-40 h-40 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
          {qr ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={qr} alt="Registration QR" className="w-full h-full object-contain" />
          ) : (
            <span className="text-xs text-gray-500">QR unavailable</span>
          )}
        </div>
      </div>
      <RegistrationPdfModal
        open={showPdf}
        onClose={() => setShowPdf(false)}
        parishName={parishName}
        registrationUrl={registrationUrl || undefined}
      />
    </motion.div>
  )
}


